import {
  FieldErrorProps,
  LabelProps,
  FieldError as RACFieldError,
  Label as RACLabel,
  Text,
  TextProps,
} from "react-aria-components";
import { CircleAlert } from "lucide-react";
import { cn } from "@/utils/helpers";

/*
 * Field parts (CreativeOS Library.lib.pen, Components / Inputs and selection,
 * style B). The label sits above in uppercase tracking; a hint under the field
 * is faint; an error says what went wrong and how to recover, after a
 * circle-alert, in the danger ink that also rings the field.
 */

/** The field surface: control glass, radius 12, no line at rest, a 2px accent ring on focus. */
export const kitFieldSurfaceClass =
  "co-glass-control rounded-card outline-offset-0 focus-within:outline-2 focus-within:outline-co-accent forced-colors:focus-within:outline-[Highlight]";

/** The same field drawn on a bare <input>, <textarea> or <select>, for spots
 *  that cannot take KitInput / KitTextarea: control glass at 12, no line at
 *  rest, the 2px accent ring on focus. Add padding and type at the call. */
export const kitBareFieldClass =
  "co-input-own-focus co-glass-control caret-co-accent rounded-card border-0 text-co-text placeholder:text-co-faint outline-offset-0 focus:outline-2 focus:outline-co-accent forced-colors:focus:outline-[Highlight] disabled:cursor-not-allowed disabled:opacity-50";

/** The ring an invalid field keeps, focused or not. */
export const kitFieldInvalidClass = "outline-2 outline-co-danger focus-within:outline-co-danger";

export const kitLabelClass = "text-[11px] font-medium uppercase tracking-[0.14em] text-co-muted";

export function Label(props: LabelProps) {
  return <RACLabel {...props} className={cn(kitLabelClass, props.className)} />;
}

export function Description(props: TextProps) {
  return (
    <Text
      {...props}
      slot="description"
      className={cn("text-[12.5px] leading-[1.45] text-co-faint", props.className)}
    />
  );
}

/** The error line under a field: the alert mark and the reason. */
export function KitFieldMessage({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn("flex items-start gap-2 text-[12.5px] leading-[1.45] text-co-danger", className)}>
      <CircleAlert aria-hidden className="mt-px size-3.5 shrink-0" />
      <span>{children}</span>
    </span>
  );
}

export function FieldError({ children, className, ...props }: FieldErrorProps) {
  return (
    <RACFieldError
      {...props}
      className={cn("flex items-start gap-2 text-[12.5px] leading-[1.45] text-co-danger", className as string)}
    >
      {(validation) => (
        <>
          <CircleAlert aria-hidden className="mt-px size-3.5 shrink-0" />
          <span>
            {typeof children === "function"
              ? children(validation)
              : (children ?? validation.validationErrors.join(" "))}
          </span>
        </>
      )}
    </RACFieldError>
  );
}
